// Placeholder for ChatBotV2 API functions
import apiClient from "@lift-admin/utils/apiClient";
import { Toastify } from 'lift-ui';
import { authFirebase } from "@lift-admin/utils";
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import { BrandMap } from "../context/ChatV2Context";

export const ADMIN_DEV_MODE = false; 

export function getApiRoute(path: string): string {
  // Normalize path to always start with a slash for matching
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (ADMIN_DEV_MODE) {
    // Remove the /liftai prefix if present, otherwise use as is
    const newPath = normalizedPath.replace(/^\/liftai/, "");
    return `http://localhost:8000${newPath}`;
  }
  else {
    return `${import.meta.env.VITE_BASEURL}${path}`;
    
  }
}

// Define API Endpoints
const API_ENDPOINTS = (() => {
  const base = "/liftai";
  return {
    AGENT_CHAT: getApiRoute(`${base}/agent/chat`),
    CREATE_SESSION: getApiRoute(`${base}/sessions`), // POST endpoint for creating a session
    GET_SESSIONS: getApiRoute(`${base}/sessions`), // GET endpoint for listing agent sessions
    SESSION_MEMORY: getApiRoute(`${base}/sessions`), // POST endpoint for getting session memory
    GET_SESSION_MEMORY: getApiRoute(`${base}/sessions/memory`), // GET endpoint for retrieving session memory
    UPDATE_SESSION: getApiRoute(`${base}/sessions`), // PUT endpoint for updating a session
    GET_MESSAGES: getApiRoute(`${base}/messages`), // GET endpoint for retrieving messages
    CREATE_MESSAGE: getApiRoute(`${base}/messages`), // POST endpoint for creating a message
    UPDATE_MESSAGE: getApiRoute(`${base}/messages`), // PUT endpoint for updating a message
    
    // Admin V2 Endpoints
    ADMIN_SESSIONS_FILTERED: getApiRoute(`${base}/admin/v2/sessions_filtered`),
    ADMIN_UPDATE_MESSAGE_METADATA: (messageRunId: string) => getApiRoute(`${base}/admin/v2/messages/${messageRunId}/metadata`),
    ADMIN_ANALYTICS_DATA: getApiRoute(`${base}/admin/v2/analytics_data`),
    ADMIN_USERS_ORDERED_BY_SESSIONS: getApiRoute(`${base}/admin/v2/users/ordered_by_sessions`),
    ADMIN_DISTINCT_BRAND_IDS: getApiRoute(`${base}/admin/v2/distinct_brand_ids`),
    ADMIN_AVAILABLE_MODELS: getApiRoute(`${base}/admin/v2/models`),
    // New analytics endpoints
    ADMIN_ANALYTICS_TIMESERIES: getApiRoute(`${base}/admin/v2/analytics/timeseries`),
    ADMIN_ANALYTICS_TRENDS: getApiRoute(`${base}/admin/v2/analytics/trends`),
    ADMIN_ANALYTICS_DETAILED: getApiRoute(`${base}/admin/v2/analytics/detailed`),
    
    // Workflow Runs Admin Endpoints
    ADMIN_WORKFLOW_RUNS: getApiRoute(`${base}/admin/v2/workflow_runs`),
    ADMIN_WORKFLOW_RUNS_SUMMARY: getApiRoute(`${base}/admin/v2/workflow_runs/summary`),
    ADMIN_DISTINCT_CHANNELS: getApiRoute(`${base}/admin/v2/distinct_channels`),
    
    // Workflow Endpoints
    WORKFLOWS: getApiRoute(`${base}/workflows`),
    WORKFLOW_BY_ID: (workflowId: string) => getApiRoute(`${base}/workflows/${workflowId}`),
    
    // User Memories Endpoint
    USER_MEMORIES: (userId: string) => getApiRoute(`${base}/user-memories/${userId}`),
    
    // Master Memory Endpoints
    MASTER_MEMORIES: getApiRoute(`${base}/master-memories`),
    MASTER_MEMORIES_SEARCH: getApiRoute(`${base}/master-memories/search`),
    MASTER_MEMORIES_SINGLE: (memoryId: string) => getApiRoute(`${base}/master-memories/single/${memoryId}`),
    MASTER_MEMORIES_BY_USER: (userId: string, agentId: string) => getApiRoute(`${base}/master-memories/${userId}/${agentId}`),
    
    // Model Registry + Configuration Endpoints
    ADMIN_MODEL_CONFIG: getApiRoute(`${base}/admin/v2/model-config`),
    ADMIN_MODEL_CONFIG_ROLE: (role: string) => getApiRoute(`${base}/admin/v2/model-config/${role}`),
    ADMIN_MODELS_REGISTRY: getApiRoute(`${base}/admin/v2/models-registry`),
    ADMIN_MODELS_REGISTRY_BY_ID: (modelId: string) => getApiRoute(`${base}/admin/v2/models-registry/${modelId}`),
    ADMIN_MODELS_REGISTRY_SYNC: getApiRoute(`${base}/admin/v2/models-registry/sync`),
    ADMIN_MODELS_REGISTRY_VERTEX_AVAILABILITY: (modelId: string) => getApiRoute(`${base}/admin/v2/models-registry/vertex-availability/${modelId}`),
  };
})();

// Admin V2 Types
export enum FeedbackTypeV2 {
  ALL = "all",
  LIKED = "liked",
  DISLIKED = "disliked",
  NONE = "none"
}

export enum GroupByV2 {
  NONE = "none",
  USER = "user",
  BRAND = "brand"
}

export interface AdminSessionV2FilterPayload {
  feedback_type?: FeedbackTypeV2;
  liked?: boolean;
  disliked?: boolean;
  no_feedback?: boolean;
  start_date?: Date;
  end_date?: Date;
  min_chat_length?: number;
  max_chat_length?: number;
  user_id?: string;
  brand_id?: string;
  channel_id?: string;
  model?: string;
  tags?: string[];
  tag_search?: string;
  bookmarked?: boolean;
  search_term?: string;
  page?: number;
  page_size?: number;
  sort_by?: "last_updated" | "created_at";
  sort_order?: "desc" | "asc";
  // New filters for session source and type
  exclude_workflow_tests?: boolean;  // Exclude sessions with metadata.workflow_test=true
  source?: "all" | "app" | "slack";  // Filter by session source
}

export interface AdminMessageMetadataUpdatePayload {
  run_id: string;
  key: string;
  value: any;
}

export interface AdminMessageFeedbackV2 {
  is_liked?: boolean;
  comment?: string;
  reasons?: string[];
}


export interface AdminSessionV2DetailResponse {
  session_id: string;
  user_id?: string;
  session_name?: string;
  created_at: Date;
  last_updated: Date;
  metadata?: Record<string, any>;
  is_active: boolean;
  chat_length?: number;
  channel_name?: string; // Channel where the session was created
  liked_message_count?: number;
  disliked_message_count?: number;
  no_feedback_message_count?: number;
  session_tags?: string[];
}

export interface AdminFilteredSessionsV2Response {
  total_sessions: number;
  data: AdminSessionV2DetailResponse[];
  page: number;
  page_size: number;
  liked_sessions_count?: number;
  disliked_sessions_count?: number;
  no_feedback_sessions_count?: number;
  total_liked_messages?: number;
  total_disliked_messages?: number;
  total_no_feedback_messages?: number;
  
  // Lists of exact run IDs for liked and disliked messages
  liked_message_run_ids?: string[];
  disliked_message_run_ids?: string[];
  no_feedback_message_run_ids?: string[];
}

export interface AdminAnalyticsV2Response {
  total_sessions: number;
  total_liked_messages: number;
  total_disliked_messages: number;
  total_no_feedback_messages: number;
  total_tags: number;
  distinct_brand_ids: number[];
  distinct_tags: string[];
}

export interface BrandDataV2Response {
  brand_id: string;
  session_count: number;
}

export interface DistinctBrandIdsV2Response {
  success: boolean;
  data: BrandDataV2Response[];
  total_distinct_brands: number;
}

// Workflow Runs Types
export interface WorkflowRunFilterPayload {
  workflow_id?: string;
  user_id?: string;
  trigger_type?: 'scheduled' | 'manual';
  start_date?: Date;
  end_date?: Date;
  brand_id?: string;
  search_term?: string;
  page?: number;
  page_size?: number;
}

export interface WorkflowRunDetailResponse {
  workflow_session_id: string;
  workflow_id: string;
  session_id: string;
  channel_id: string;
  workflow_session_created_at?: Date;
  trigger_type?: string;
  workflow_name?: string;
  workflow_prompt?: string;
  workflow_schedule?: string;
  workflow_agent?: string;
  workflow_created_by?: number;
  workflow_brand_map?: Record<string, any>;
  workflow_notify?: string; // Notification method: slack, email, both, or none
  workflow_metadata?: Record<string, any>; // Workflow metadata including model info
  workflow_model?: string; // Model used (extracted from metadata)
  session_name?: string;
  session_first_msg?: string;
  session_is_active?: boolean;
  session_metadata?: Record<string, any>;
  session_created_at?: Date;
  session_updated_at?: Date;
  creator_email?: string;
  shared_user_ids?: string[] | number[]; // User IDs the workflow is shared with
  is_disabled?: boolean; // Whether the workflow is disabled
}

export interface WorkflowRunsFilteredResponse {
  total_runs: number;
  data: WorkflowRunDetailResponse[];
  page: number;
  page_size: number;
  total_workflows?: number;
  scheduled_runs_count?: number;
  manual_runs_count?: number;
}

export interface WorkflowSummary {
  workflow_id: string;
  name: string;
  schedule: string;
  agent: string;
  total_runs: number;
  last_run_at?: Date;
  created_by: number;
  brand_map?: Record<string, any>;
}

export interface WorkflowAnalyticsSummary {
  total_workflows: number;
  total_runs: number;
  scheduled_runs: number;
  manual_runs: number;
  active_workflows: number;
  runs_last_24h: number;
  runs_last_7d: number;
  top_workflows: WorkflowSummary[];
}

export interface DistinctChannelsResponse {
  success: boolean;
  data: {
    channel_id: string;
    channel_name: string;
    session_count: number;
  }[];
}

// User Memories Types
export interface UserMemory {
  memory_id: string;
  memory: string;
  topics: string[] | null;
  input: string;
  last_updated: string | null;
}

export type UserMemoriesResponse = UserMemory[];

// Session Memory Types
export interface SessionMemoryMessage {
  role: string;
  content: string;
  created_at: number;
  from_history: boolean;
  stop_after_tool_call: boolean;
  metrics?: {
    time?: number;
    input_tokens?: number;
    total_tokens?: number;
    output_tokens?: number;
    time_to_first_token?: number;
  };
}

export interface SessionMemoryRun {
  message: SessionMemoryMessage;
  response: {
    event: string;
    model: string;
    run_id: string;
    content: string;
    metrics: {
      time: number[];
      audio_tokens: number[];
      input_tokens: number[];
      total_tokens: number[];
      cached_tokens: number[];
      output_tokens: number[];
      prompt_tokens: number[];
      reasoning_tokens: number[];
      completion_tokens: number[];
      input_audio_tokens: number[];
      output_audio_tokens: number[];
      time_to_first_token: number[];
    };
    team_id: string;
    messages: SessionMemoryMessage[];
    created_at: number;
    session_id: string;
    content_type: string;
    member_responses: any[];
  };
  member_responses: any;
}

export interface SessionMemoryUserMemory {
  input: string;
  memory: string;
}

export interface SessionMemoryResponse {
  runs: SessionMemoryRun[];
  user_id: string;
  memories: SessionMemoryUserMemory[];
  messages: SessionMemoryMessage[];
  create_user_memories: boolean;
  update_user_memories_after_run: boolean;
  update_system_message_on_change: boolean;
}

// Existing types
export interface ChatMessage {
  message: string;
  session_id: string;
  user_id: string;
  brand_map: BrandMap;
  is_singleBrand: boolean;
  user_state?: {
    [key: string]: any;
  };
  agency_mode?: boolean;
}

export interface MessageRequest {
  session_id: string;
  user_id: string;
  user_state?: Record<string, any>;
}

export interface SessionRequest {
  user_id: string;
  session_name?: string; // Optional session name
  metadata?: {
    // Optional metadata
    [key: string]: any;
  };
}

export interface SessionResponse {
  session_id: string;
  created_at: string;
  user_id: string;
}

export interface AgentSession {
  session_id: string;
  user_id: string;
  memory: any;
  session_data: any;
  extra_data: any;
  created_at: string;
  updated_at: string;
  agent_id: string;
  team_session_id: string;
  agent_data: any;
  team_id: string;
  team_data: any;
  session_name?: string; // Optional session name
}

export interface AgentSessionUpdatePayload {
  memory?: any;
  session_data?: any;
  extra_data?: any;
  agent_id?: string;
  team_session_id?: string;
  agent_data?: any;
  session_name?: string; // Optional session name
  metadata?: {
    // Optional metadata
    [key: string]: any;
  };
  is_active?: boolean; // Optional is_active flag
}

export interface MessageResponse {
  run_id: string;
  session_id: string;
  updated_at?: number;
  metadata?: Record<string, any>;
}

export interface MessageUpdatePayload {
  metadata?: Record<string, any>;
}

export interface MessageCreatePayload {
  run_id: string;
  session_id: string;
  metadata?: Record<string, any>;
}

// Simple in-memory cache for session data to avoid duplicate calls per session
const sessionDataCache: Record<string, any> = {};
const sessionDataPromiseCache: Record<string, Promise<any>> = {};

/**
 * Get user information from localStorage
 */
const getUserInfo = () => {
  let userId = "";

  try {
    // Get user from localStorage
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      userId = user.id || "";
    }
  } catch (error) {
    console.error("Error getting user from localStorage:", error);
  }

  return { userId };
};

// Error type guard
const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

// Admin V2 API Functions

/**
 * Get filtered sessions using admin v2 endpoint
 */
export async function getFilteredSessions(
  filters: AdminSessionV2FilterPayload
): Promise<AdminFilteredSessionsV2Response> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ADMIN_SESSIONS_FILTERED,
      filters
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch filtered sessions: ${response.status}`);
      throw new Error(`Failed to fetch filtered sessions: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching filtered sessions");
    console.error("Error fetching filtered sessions:", error);
    throw error;
  }
}

/**
 * Update message metadata using admin v2 endpoint
 */
export async function updateMessageMetadata(
  messageRunId: string,
  key: string,
  value: any
): Promise<{ status: string; updated_metadata: Record<string, any> }> {
  try {
    const payload: AdminMessageMetadataUpdatePayload = {
      run_id: messageRunId,
      key,
      value
    };

    const response = await apiClient.post(
      API_ENDPOINTS.ADMIN_UPDATE_MESSAGE_METADATA(messageRunId),
      payload
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to update message metadata: ${response.status}`);
      throw new Error(`Failed to update message metadata: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error updating message metadata");
    console.error("Error updating message metadata:", error);
    throw error;
  }
}

/**
 * Get analytics data using admin v2 endpoint
 */
export async function getAnalyticsData(
  sessionId?: string
): Promise<AdminAnalyticsV2Response> {
  try {
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_ANALYTICS_DATA,
      { params }
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch analytics data: ${response.status}`);
      throw new Error(`Failed to fetch analytics data: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching analytics data");
    console.error("Error fetching analytics data:", error);
    throw error;
  }
}

/**
 * Get users ordered by session count using admin v2 endpoint
 */
export async function getUsersOrderedBySessions(
  limit?: number
): Promise<string[]> {
  try {
    const params = limit ? { limit } : {};
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_USERS_ORDERED_BY_SESSIONS,
      { params }
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch users: ${response.status}`);
      throw new Error(`Failed to fetch users: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching users");
    console.error("Error fetching users:", error);
    throw error;
  }
}

/**
 * User info interface for admin panel
 */
export interface AdminUserInfo {
  id: string;
  name: string;
  email: string;
  profile_picture?: string;
  image?: string;
}

/**
 * Get all users with their details
 */
export async function getAllUsersWithDetails(): Promise<AdminUserInfo[]> {
  try {
    const response = await apiClient.get("/user/v1/admin/all");

    if (response.status !== 200) {
      throw new Error(`Failed to fetch users: ${response.status}`);
    }

    return response.data.response || [];
  } catch (error) {
    console.error("Error fetching all users:", error);
    throw error;
  }
}

/**
 * Get distinct brand IDs using admin v2 endpoint
 */
export async function getDistinctBrandIds(): Promise<DistinctBrandIdsV2Response> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_DISTINCT_BRAND_IDS
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch brand IDs: ${response.status}`);
      throw new Error(`Failed to fetch brand IDs: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching brand IDs");
    console.error("Error fetching brand IDs:", error);
    throw error;
  }
}

/**
 * Get available models using admin v2 endpoint
 */
export async function getAvailableModels(): Promise<string[]> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_AVAILABLE_MODELS
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch available models: ${response.status}`);
      throw new Error(`Failed to fetch available models: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching available models");
    console.error("Error fetching available models:", error);
    throw error;
  }
}

/**
 * Get filtered workflow runs using admin v2 endpoint
 */
export async function getFilteredWorkflowRuns(
  filters: WorkflowRunFilterPayload
): Promise<WorkflowRunsFilteredResponse> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ADMIN_WORKFLOW_RUNS,
      filters
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch workflow runs: ${response.status}`);
      throw new Error(`Failed to fetch workflow runs: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching workflow runs");
    console.error("Error fetching workflow runs:", error);
    throw error;
  }
}

/**
 * Get workflow runs summary analytics
 */
export async function getWorkflowRunsSummary(): Promise<WorkflowAnalyticsSummary> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_WORKFLOW_RUNS_SUMMARY
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch workflow summary: ${response.status}`);
      throw new Error(`Failed to fetch workflow summary: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching workflow summary");
    console.error("Error fetching workflow summary:", error);
    throw error;
  }
}

/**
 * Get distinct channels for filtering
 */
export async function getDistinctChannels(): Promise<DistinctChannelsResponse> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_DISTINCT_CHANNELS
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch channels: ${response.status}`);
      throw new Error(`Failed to fetch channels: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching channels");
    console.error("Error fetching channels:", error);
    throw error;
  }
}

/**
 * Get user memories for a specific user ID
 */
export async function getUserMemories(userId: string): Promise<UserMemoriesResponse> {
  try {
    console.log(`[getUserMemories] Fetching memories for user ID: ${userId}`);

    if (!userId) {
      throw new Error("User ID is required");
    }

    const response = await apiClient.get(
      API_ENDPOINTS.USER_MEMORIES(userId)
    );

    if (response.status !== 200) {
      console.error(
        `[getUserMemories] Failed with status: ${response.status}`
      );
      Toastify("error", `Failed to fetch user memories: ${response.status}`);
      throw new Error(`Failed to fetch user memories: ${response.status}`);
    }

    const memories = response.data;
    console.log(`[getUserMemories] Retrieved ${memories.length} memories for user ${userId}`);
    
    return memories;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "User memories fetching timed out. Please try again.");
      throw new Error("User memories fetching timed out");
    }
    Toastify("error", "Error fetching user memories");
    console.error("Error fetching user memories:", error);
    throw error;
  }
}

/**
 * Create a new session
 */
export async function createSession(
  session_name?: string,
  metadata?: { [key: string]: any }
): Promise<SessionResponse> {
  const { userId } = getUserInfo();

  if (!userId) {
    throw new Error("User ID not found in localStorage");
  }

  try {
    // Use apiClient instead of local backend URL
    const payload: SessionRequest = {
      user_id: String(userId),
    };

    if (session_name) {
      payload.session_name = session_name;
    }

    if (metadata) {
      payload.metadata = metadata;
    }

    const response = await apiClient.post(
      API_ENDPOINTS.CREATE_SESSION,
      payload
    );

    if (response.status !== 200 && response.status !== 201) {
      Toastify("error", `Failed to create session: ${response.status}`);
      throw new Error(`Failed to create session: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Session creation timed out. Please try again.");
      throw new Error("Session creation timed out");
    }
    Toastify("error", "Error creating session");
    console.error("Error creating session:", error);
    throw error;
  }
}

/**
 * Fetch all agent sessions for the current user
 */
export async function getAgentSessions(): Promise<AgentSession[]> {
  const { userId } = getUserInfo();

  if (!userId) {
    throw new Error("User ID not found in localStorage");
  }

  try {
    console.log(`[getAgentSessions] Fetching sessions for user ID: ${userId}`);

    const response = await apiClient.get(API_ENDPOINTS.GET_SESSIONS, {
      params: { user_id: String(userId) },
    });

    if (response.status !== 200) {
      console.error(
        `[getAgentSessions] Failed with status: ${response.status}`
      );
      Toastify("error", `Failed to fetch sessions: ${response.status}`);
      throw new Error(`Failed to fetch sessions: ${response.status}`);
    }

    const sessions = response.data;
    console.log(`[getAgentSessions] Retrieved ${sessions.length} sessions`);
    // Log the first session data to check if session_name is present
    if (sessions.length > 0) {
      console.log(
        `[getAgentSessions] First session example:`,
        JSON.stringify(sessions[0], null, 2)
      );
    }

    return sessions;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Session fetching timed out. Please try again.");
      throw new Error("Session fetching timed out");
    }
    Toastify("error", "Error fetching sessions");
    console.error("Error fetching sessions:", error);
    throw error;
  }
}

/**
 * Get session memory data using POST to /sessions/{session_id}
 */
export async function getSessionMemory(sessionId: string): Promise<any> {
  const { userId } = getUserInfo();

  if (!userId) {
    throw new Error("User ID not found in localStorage");
  }

  try {
    // Create payload exactly matching the MessageRequest model from FastAPI
    const payload: MessageRequest = {
      session_id: sessionId,
      user_id: String(userId),
      user_state: {},
    };

    console.log(
      `Sending POST request to fetch session memory for session ${sessionId}`
    );

    // Get Firebase auth token for authorization
    const user = authFirebase.currentUser;
    let headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (user) {
      const idToken = await user.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    }

    // Use apiClient instead of local backend URL
    const apiUrl = `${API_ENDPOINTS.SESSION_MEMORY}/${sessionId}`;
    console.log("Request URL:", apiUrl);

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

    try {
      const response = await apiClient.post(apiUrl, payload, {
        headers,
        signal: controller.signal,
      });

      // Clear the timeout
      clearTimeout(timeoutId);

      if (response.status !== 200) {
        console.error(
          `Session memory request failed with status: ${response.status}`
        );
        Toastify("error", `Failed to fetch session memory: ${response.status}`);
        throw new Error(`Failed to fetch session memory: ${response.status}`);
      }

      // Unwrap memory and preserve view_only flag if provided by backend
      const data = response.data;
      if (data.memory && data.view_only) {
        // For view-only access, return the memory object with a view_only flag
        return {
          ...data.memory,
          view_only: data.view_only,
        };
      }
      return data;
    } catch (error) {
      // Clear the timeout in case of error
      clearTimeout(timeoutId);
      throw error; // Re-throw for outer catch
    }
  } catch (error) {
    if (isError(error) && (error.name === "TimeoutError" || error.name === "AbortError")) {
      console.error("Session memory fetching timed out:", error);
      Toastify("error", "Session memory fetching timed out. Please try again.");
      throw new Error("Session memory fetching timed out");
    }
    console.error("Error fetching session memory:", error);
    Toastify("error", "Error fetching session memory");
    throw error;
  }
}

/**
 * Update a session using PUT to /sessions/{session_id}
 */
export async function updateSession(
  sessionId: string,
  payload: AgentSessionUpdatePayload
): Promise<AgentSession> {
  try {
    console.log(`[updateSession] Updating session ID: ${sessionId}`);
    console.log(`[updateSession] Payload:`, JSON.stringify(payload, null, 2));

    const response = await apiClient.put(
      `${API_ENDPOINTS.UPDATE_SESSION}/${sessionId}`,
      payload
    );

    if (response.status !== 200) {
      console.error(`[updateSession] Failed with status: ${response.status}`);
      Toastify("error", `Failed to update session: ${response.status}`);
      throw new Error(`Failed to update session: ${response.status}`);
    }

    const responseData = response.data;
    console.log(
      `[updateSession] Success response:`,
      JSON.stringify(responseData, null, 2)
    );

    return responseData;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Session update timed out. Please try again.");
      throw new Error("Session update timed out");
    }
    Toastify("error", "Error updating session");
    console.error("Error updating session:", error);
    throw error;
  }
}

/**
 * Send a message to the chatbot
 */
export async function sendChatMessage(
  message: string,
  sessionId: string,
  signal?: AbortSignal,
  agencyMode: boolean = false,
  brandMap: BrandMap = {},
  shouldUpdateSessionName?: boolean, // New flag to indicate if session name should be updated
  editMessageIndex?: number // New parameter for edit message index
): Promise<any> {
  try {
    const { userId } = getUserInfo();

    if (!userId) {
      throw new Error("User ID not found in localStorage");
    }

    // Match the ChatRequest format from the FastAPI spec exactly
    const payload: ChatMessage & { edit_message_index?: number } = {
      message,
      session_id: sessionId,
      user_id: String(userId),
      brand_map: brandMap,
      is_singleBrand: Object.keys(brandMap).length === 1,
      user_state: {}, // Empty object instead of additionalProp1
      agency_mode: agencyMode,
    };

    // Add edit_message_index to payload if provided
    if (editMessageIndex !== undefined) {
      payload.edit_message_index = editMessageIndex;
    }

    const user = authFirebase.currentUser;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      // Change Accept header to expect a stream of events
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      Connection: "keep-alive",
    };

    if (user) {
      const idToken = await user.getIdToken();
      (headers as any)["Authorization"] = `Bearer ${idToken}`;
    }

    // Create a timeout controller that aborts after our timeout
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 600000); // 10 min timeout

    // Merge the provided signal with the timeout signal to ensure fetch always aborts
    let requestSignal: AbortSignal;
    if (signal) {
      // Create a merged controller
      const mergedController = new AbortController();
      // Abort merged when either signal fires
      const abortMerged = () => mergedController.abort();
      signal.addEventListener("abort", abortMerged);
      timeoutController.signal.addEventListener("abort", abortMerged);
      // Clean up listeners when merged aborts
      mergedController.signal.addEventListener("abort", () => {
        signal.removeEventListener("abort", abortMerged);
        timeoutController.signal.removeEventListener("abort", abortMerged);
      });
      requestSignal = mergedController.signal;
    } else {
      requestSignal = timeoutController.signal;
    }

    // Use apiClient instead of local backend URL
    const apiUrl = API_ENDPOINTS.AGENT_CHAT;
    console.log("Sending chat request to:", apiUrl);

    // For stream responses, we need to use fetch instead of apiClient directly
    // Create the URL using the baseURL from apiClient
    const baseURL = apiClient.defaults.baseURL || "";
    // If apiUrl is already a full URL (starts with http), use it directly.
    // Otherwise, prepend the baseURL from apiClient.
    const url = apiUrl.startsWith("http") ? apiUrl : `${baseURL}/${apiUrl}`;

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      signal: requestSignal,
    });

    // Clear the timeout since the request completed
    clearTimeout(timeoutId);

    if (!response.ok) {
      if (!signal?.aborted) {
        Toastify("error", `Failed to send message: ${response.status}`);
      }
      throw new Error(`Failed to send message: ${response.status}`);
    }

    // Ensure response has a readable body
    if (!response.body) {
      throw new Error("Response body is null or undefined");
    }

    // If indicated, update the session name with the message content
    if (shouldUpdateSessionName && message) {
      try {
        console.log(
          `[sendChatMessage] Attempting to update session name for session ID: ${sessionId}`
        );
        console.log(
          `[sendChatMessage] First message content (truncated): "${message.substring(0, 50)}${message.length > 50 ? "..." : ""}"`
        );
        console.log(
          `[sendChatMessage] shouldUpdateSessionName flag is: ${shouldUpdateSessionName}`
        );

        // Use updateSessionMetadata instead of updateSession to preserve other metadata values
        await updateSessionMetadata(sessionId, {
          session_name: message,
          brandMap: brandMap,
          agencyMode: agencyMode,
        });

        console.log(
          `[sendChatMessage] Session name and metadata update completed successfully for session ID: ${sessionId}`
        );
      } catch (updateError) {
        console.error(
          `[sendChatMessage] Failed to update session name for session ID: ${sessionId}`,
          updateError
        );
        // Do not throw here, as the primary message was sent successfully
        // Optionally, notify the user of the partial success if needed
        Toastify(
          "warning",
          "Message sent, but failed to update session metadata."
        );
      }
    } else {
      // Always update brandMap and agencyMode in session metadata even for non-first messages
      try {
        console.log(
          `[sendChatMessage] Updating metadata for session ID: ${sessionId}`
        );

        await updateSessionMetadata(sessionId, {
          brandMap: brandMap,
          agencyMode: agencyMode,
        });

        console.log(
          `[sendChatMessage] Metadata update completed successfully for session ID: ${sessionId}`
        );
      } catch (updateError) {
        console.error(
          `[sendChatMessage] Failed to update metadata for session ID: ${sessionId}`,
          updateError
        );
        Toastify(
          "warning",
          "Message sent, but failed to update session metadata."
        );
      }
    }

    return response;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      console.log("Message generation aborted or timed out");
      Toastify("error", "Request timed out. Please try again.");
      return null;
    }
    console.error("Error sending message:", error);
    Toastify("error", "Error sending message");
    throw error;
  }
}

/**
 * Get session memory using GET to /sessions/memory/{session_id}
 */
export async function getSessionMemoryJson(sessionId: string): Promise<any> {
  try {
    console.log(`[API] Fetching session memory JSON for session: ${sessionId}`);

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    const response = await apiClient.get(
      `${API_ENDPOINTS.GET_SESSION_MEMORY}/${sessionId}`
    );

    if (response.status !== 200) {
      console.error(
        `[API] Failed to fetch session memory with status: ${response.status}`
      );

      if (response.status === 404) {
        console.log(
          `[API] Session memory not found (404), returning empty object`
        );
        return {};
      }

      Toastify("error", `Failed to fetch session memory: ${response.status}`);
      throw new Error(`Failed to fetch session memory: ${response.status}`);
    }

    const memoryData = response.data;
    console.log(`[API] Session memory fetched successfully`);
    return memoryData;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Session memory fetching timed out. Please try again.");
      throw new Error("Session memory fetching timed out");
    }
    console.error(`[API] Error fetching session memory:`, error);
    Toastify("error", "Error fetching session memory");
    throw error;
  }
}

/**
 * Fetch session data from the new dedicated endpoint
 */
export async function getSessionData(sessionId: string): Promise<any> {
  // Return cached data if available
  if (sessionDataCache[sessionId]) {
    return sessionDataCache[sessionId];
  }

  // If a fetch is already in progress for this session, wait for it
  if (sessionDataPromiseCache[sessionId] !== undefined) {
    return await sessionDataPromiseCache[sessionId];
  }

  // Otherwise, start a new fetch
  const fetchPromise = (async () => {
    try {
      console.log(`[API] Fetching session data for session: ${sessionId}`);

      if (!sessionId) {
        throw new Error("Session ID is required");
      }

      const response = await apiClient.get(
        getApiRoute(`/liftai/sessions/${sessionId}`)
      );

      if (response.status !== 200) {
        console.error(
          `[API] Failed to fetch session data with status: ${response.status}`
        );

        if (response.status === 404) {
          console.log(
            `[API] Session metadata not found (404), returning empty metadata object`
          );
          return { metadata: {} };
        }

        throw new Error(`Failed to fetch session data: ${response.status}`);
      }

      const sessionData = response.data;
      console.log(`[API] Session data fetched successfully:`);

      // Cache the data for subsequent requests
      sessionDataCache[sessionId] = sessionData;
      return sessionData;
    } catch (error) {
      console.error(`[API] Error fetching session data:`, error);
      return { metadata: {} };
    } finally {
      // Remove the promise cache once resolved/rejected to allow refetch if needed later
      delete sessionDataPromiseCache[sessionId];
    }
  })();

  sessionDataPromiseCache[sessionId] = fetchPromise;
  return fetchPromise;
}

/**
 * Helper function to update session metadata without losing existing data
 * This ensures different components can update different parts of metadata without overwriting each other
 */
export async function updateSessionMetadata(
  sessionId: string,
  newMetadata: Record<string, any>
): Promise<AgentSession> {
  try {
    console.log(`[API] Updating metadata for session: ${sessionId}`);

    // First, get the current session data to access existing metadata using the new endpoint
    const sessionData = await getSessionData(sessionId);

    // Extract current metadata
    let currentMetadata: Record<string, any> = {};
    if (sessionData && sessionData.metadata) {
      try {
        // Handle both string and object formats
        currentMetadata =
          typeof sessionData.metadata === "string"
            ? JSON.parse(sessionData.metadata)
            : sessionData.metadata;

        console.log(
          `[API] Current metadata from dedicated endpoint:`,
          currentMetadata
        );
      } catch (parseError) {
        console.error(`[API] Error parsing existing metadata:`, parseError);
        // Continue with empty object if parsing fails
      }
    }

    // Extract session_name and is_active from newMetadata if present
    const { session_name, is_active, ...otherMetadata } = newMetadata;

    // Merge existing metadata with remaining newMetadata
    const mergedMetadata = {
      ...currentMetadata,
      ...otherMetadata,
      lastUpdated: new Date().toISOString(),
    };

    console.log(`[API] Merged metadata:`, mergedMetadata);

    // Create update payload with top-level properties for session_name and is_active
    const updatePayload: AgentSessionUpdatePayload = {
      metadata: mergedMetadata,
    };

    // Add session_name and is_active as top-level properties if they exist
    if (session_name !== undefined) {
      updatePayload.session_name = session_name;
    }

    if (is_active !== undefined) {
      updatePayload.is_active = is_active;
    }

    // Update the session with the correct payload
    const updatedSession = await updateSession(sessionId, updatePayload);

    // Invalidate / refresh cache with updated data
    sessionDataCache[sessionId] = {
      ...sessionData,
      ...updatedSession,
      metadata: mergedMetadata,
    };

    return updatedSession;
  } catch (error) {
    console.error(`[API] Error updating session metadata:`, error);
    throw error;
  }
}

/**
 * Fetch messages for a session
 */
export async function getMessages(
  sessionId: string
): Promise<MessageResponse[]> {
  try {
    const response = await apiClient.get(
      `${API_ENDPOINTS.GET_MESSAGES}/${sessionId}`
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to fetch messages: ${response.status}`);
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    Toastify("error", "Error fetching messages");
    console.error("Error fetching messages:", error);
    throw error;
  }
}

/**
 * Create a new message
 */
export async function createMessage(
  payload: MessageCreatePayload
): Promise<MessageResponse> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.CREATE_MESSAGE,
      payload
    );

    if (response.status !== 200 && response.status !== 201) {
      Toastify("error", `Failed to create message: ${response.status}`);
      throw new Error(`Failed to create message: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    Toastify("error", "Error creating message");
    console.error("Error creating message:", error);
    throw error;
  }
}

/**
 * Update an existing message
 */
export async function updateMessage(
  runId: string,
  payload: MessageUpdatePayload
): Promise<MessageResponse> {
  try {
    const response = await apiClient.put(
      `${API_ENDPOINTS.UPDATE_MESSAGE}/${runId}`,
      payload
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to update message: ${response.status}`);
      throw new Error(`Failed to update message: ${response.status}`);
    }

    return response.data;
  } catch (error) {
    Toastify("error", "Error updating message");
    console.error("Error updating message:", error);
    throw error;
  }
}

/**
 * Update a message with feedback data
 */
export async function updateMessageFeedback(
  runId: string,
  sessionId: string,
  feedback: {
    reasons: string[];
    comment: string;
  }
): Promise<MessageResponse> {
  try {
    console.log(
      `[updateMessageFeedback] Processing feedback for message ${runId}`
    );

    // Create the feedback metadata
    const metadata = {
      user_response: {
        reasons: feedback.reasons,
        comment: feedback.comment,
        timestamp: Date.now(),
      },
      is_liked: false, // Set to false because this is feedback from thumbs-down
    };

    try {
      // First try to update the existing message
      console.log(
        `[updateMessageFeedback] Attempting to update existing message`
      );
      return await updateMessage(runId, { metadata });
    } catch (updateError) {
      // If update fails (likely message doesn't exist), create a new message
      console.log(
        `[updateMessageFeedback] Update failed, creating new message record`,
        updateError
      );

      // Create a new message with the feedback metadata
      return await createMessage({
        run_id: runId,
        session_id: sessionId,
        metadata,
      });
    }
  } catch (error) {
    console.error(
      "[updateMessageFeedback] Error updating message feedback:",
      error
    );
    Toastify("error", "Failed to save feedback");
    throw error;
  }
}

/**
 * Fetch time series analytics data
 */
export async function getTimeSeriesAnalytics(
  params: {
    time_range?: string;
    granularity?: string;
    start_date?: string;
    end_date?: string;
    brand_id?: string;
    user_id?: string;
  } = {}
): Promise<any> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_ANALYTICS_TIMESERIES,
      { params }
    );
    if (response.status !== 200) {
      Toastify("error", `Failed to fetch time series analytics: ${response.status}`);
      throw new Error(`Failed to fetch time series analytics: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching time series analytics");
    console.error("Error fetching time series analytics:", error);
    throw error;
  }
}

/**
 * Fetch trends analytics data
 */
export async function getTrendsAnalytics(
  compare_period: string = 'last_7d'
): Promise<any> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_ANALYTICS_TRENDS,
      { params: { compare_period } }
    );
    if (response.status !== 200) {
      Toastify("error", `Failed to fetch trends analytics: ${response.status}`);
      throw new Error(`Failed to fetch trends analytics: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching trends analytics");
    console.error("Error fetching trends analytics:", error);
    throw error;
  }
}

/**
 * Fetch detailed analytics data
 */
export async function getDetailedAnalytics(
  params: {
    time_range?: string;
    start_date?: string;
    end_date?: string;
  } = {}
): Promise<any> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_ANALYTICS_DETAILED,
      { params }
    );
    if (response.status !== 200) {
      Toastify("error", `Failed to fetch detailed analytics: ${response.status}`);
      throw new Error(`Failed to fetch detailed analytics: ${response.status}`);
    }
    return response.data;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Request timed out. Please try again.");
      throw new Error("Request timed out");
    }
    Toastify("error", "Error fetching detailed analytics");
    console.error("Error fetching detailed analytics:", error);
    throw error;
  }
}

/**
 * Fetch messages by specific run IDs
 */
export async function getMessagesByRunIds(
  runIds: string[]
): Promise<MessageResponse[]> {
  try {
    if (!runIds || runIds.length === 0) {
      return [];
    }

    // Make requests for each run ID
    const messagePromises = runIds.map(async (runId) => {
      try {
        const response = await apiClient.get(
          `${API_ENDPOINTS.GET_MESSAGES}/${runId}`
        );
        if (response.status === 200) {
          return response.data;
        }
        return null;
      } catch (error) {
        console.warn(`Failed to fetch message with run ID ${runId}:`, error);
        return null;
      }
    });

    const messages = await Promise.all(messagePromises);
    return messages.filter(message => message !== null);
  } catch (error) {
    console.error("Error fetching messages by run IDs:", error);
    throw error;
  }
}

/**
 * Categorize messages by feedback type using run IDs from filtered sessions response
 */
export function categorizeMessagesByFeedback(
  messages: MessageResponse[],
  filteredSessionsResponse: AdminFilteredSessionsV2Response
): {
  liked: MessageResponse[];
  disliked: MessageResponse[];
  noFeedback: MessageResponse[];
  other: MessageResponse[];
} {
  const {
    liked_message_run_ids = [],
    disliked_message_run_ids = [],
    no_feedback_message_run_ids = []
  } = filteredSessionsResponse;

  const likedSet = new Set(liked_message_run_ids);
  const dislikedSet = new Set(disliked_message_run_ids);
  const noFeedbackSet = new Set(no_feedback_message_run_ids);

  const categorized = {
    liked: [] as MessageResponse[],
    disliked: [] as MessageResponse[],
    noFeedback: [] as MessageResponse[],
    other: [] as MessageResponse[]
  };

  messages.forEach(message => {
    if (likedSet.has(message.run_id)) {
      categorized.liked.push(message);
    } else if (dislikedSet.has(message.run_id)) {
      categorized.disliked.push(message);
    } else if (noFeedbackSet.has(message.run_id)) {
      categorized.noFeedback.push(message);
    } else {
      categorized.other.push(message);
    }
  });

  return categorized;
}

/**
 * Get enhanced chat history with feedback categorization
 */
export async function getEnhancedChatHistory(
  sessionId: string,
  filteredSessionsResponse?: AdminFilteredSessionsV2Response
): Promise<{
  messages: MessageResponse[];
  categorized?: {
    liked: MessageResponse[];
    disliked: MessageResponse[];
    noFeedback: MessageResponse[];
    other: MessageResponse[];
  };
}> {
  try {
    // Fetch all messages for the session
    const messages = await getMessages(sessionId);

    // If we have filtered sessions response with run IDs, categorize the messages
    if (filteredSessionsResponse) {
      const categorized = categorizeMessagesByFeedback(messages, filteredSessionsResponse);
      return { messages, categorized };
    }

    return { messages };
  } catch (error) {
    console.error("Error fetching enhanced chat history:", error);
    throw error;
  }
}

/**
 * Check if a message run ID has specific feedback type
 */
export function getMessageFeedbackType(
  runId: string,
  filteredSessionsResponse: AdminFilteredSessionsV2Response
): 'liked' | 'disliked' | 'no_feedback' | 'unknown' {
  const {
    liked_message_run_ids = [],
    disliked_message_run_ids = [],
    no_feedback_message_run_ids = []
  } = filteredSessionsResponse;

  if (liked_message_run_ids.includes(runId)) {
    return 'liked';
  } else if (disliked_message_run_ids.includes(runId)) {
    return 'disliked';
  } else if (no_feedback_message_run_ids.includes(runId)) {
    return 'no_feedback';
  }
  
  return 'unknown';
}

/**
 * Get session memory using the new format that includes runs
 */
export async function getSessionMemoryV2(sessionId: string): Promise<SessionMemoryResponse> {
  try {
    console.log(`[API] Fetching session memory v2 for session: ${sessionId}`);

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    const response = await apiClient.get(
      `${API_ENDPOINTS.GET_SESSION_MEMORY}/${sessionId}`
    );

    if (response.status !== 200) {
      console.error(
        `[API] Failed to fetch session memory with status: ${response.status}`
      );

      if (response.status === 404) {
        console.log(
          `[API] Session memory not found (404), returning empty object`
        );
        return {
          runs: [],
          user_id: "",
          memories: [],
          messages: [],
          create_user_memories: false,
          update_user_memories_after_run: false,
          update_system_message_on_change: false
        };
      }

      Toastify("error", `Failed to fetch session memory: ${response.status}`);
      throw new Error(`Failed to fetch session memory: ${response.status}`);
    }

    const memoryData = response.data;
    console.log(`[API] Session memory v2 fetched successfully`);
    return memoryData;
  } catch (error) {
    if (isError(error) && error.name === "AbortError") {
      Toastify("error", "Session memory fetching timed out. Please try again.");
      throw new Error("Session memory fetching timed out");
    }
    console.error(`[API] Error fetching session memory v2:`, error);
    Toastify("error", "Error fetching session memory");
    throw error;
  }
}

/**
 * Convert session memory runs to chat messages format with feedback indicators
 */
export function convertSessionMemoryToChatMessages(
  sessionMemory: SessionMemoryResponse,
  filteredSessionsResponse?: AdminFilteredSessionsV2Response
): Array<{
  role: string;
  content: string;
  timestamp: Date;
  run_id?: string;
  feedback?: {
    type: 'liked' | 'disliked' | 'no_feedback' | 'unknown';
    is_liked?: boolean;
  };
  metrics?: {
    time?: number;
    input_tokens?: number;
    total_tokens?: number;
    output_tokens?: number;
    time_to_first_token?: number;
  };
}> {
  const chatMessages: Array<{
    role: string;
    content: string;
    timestamp: Date;
    run_id?: string;
    feedback?: {
      type: 'liked' | 'disliked' | 'no_feedback' | 'unknown';
      is_liked?: boolean;
    };
    metrics?: any;
  }> = [];

  sessionMemory.runs.forEach(run => {
    // Add user message
    chatMessages.push({
      role: run.message.role,
      content: run.message.content,
      timestamp: new Date(run.message.created_at * 1000),
      metrics: run.message.metrics
    });

    // Add assistant response with feedback info
    const feedbackType = filteredSessionsResponse 
      ? getMessageFeedbackType(run.response.run_id, filteredSessionsResponse)
      : 'unknown';

    chatMessages.push({
      role: 'assistant',
      content: run.response.content,
      timestamp: new Date(run.response.created_at * 1000),
      run_id: run.response.run_id,
      feedback: {
        type: feedbackType,
        is_liked: feedbackType === 'liked' ? true : feedbackType === 'disliked' ? false : undefined
      },
      metrics: {
        time: run.response.metrics.time[run.response.metrics.time.length - 1],
        input_tokens: run.response.metrics.input_tokens[run.response.metrics.input_tokens.length - 1],
        total_tokens: run.response.metrics.total_tokens[run.response.metrics.total_tokens.length - 1],
        output_tokens: run.response.metrics.output_tokens[run.response.metrics.output_tokens.length - 1],
        time_to_first_token: run.response.metrics.time_to_first_token[run.response.metrics.time_to_first_token.length - 1]
      }
    });
  });

  return chatMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/**
 * Get enhanced chat history using the new session memory format
 */
export async function getEnhancedChatHistoryV2(
  sessionId: string,
  filteredSessionsResponse?: AdminFilteredSessionsV2Response
): Promise<{
  messages: Array<{
    role: string;
    content: string;
    timestamp: Date;
    run_id?: string;
    feedback?: {
      type: 'liked' | 'disliked' | 'no_feedback' | 'unknown';
      is_liked?: boolean;
    };
    metrics?: any;
  }>;
  sessionMemory: SessionMemoryResponse;
  categorized?: {
    liked: Array<any>;
    disliked: Array<any>;
    noFeedback: Array<any>;
    other: Array<any>;
  };
}> {
  try {
    // Fetch session memory in new format
    const sessionMemory = await getSessionMemoryV2(sessionId);
    
    // Convert to chat messages format
    const messages = convertSessionMemoryToChatMessages(sessionMemory, filteredSessionsResponse);

    // If we have filtered sessions response, categorize messages by run IDs
    let categorized;
    if (filteredSessionsResponse) {
      const assistantMessages = messages.filter(msg => msg.role === 'assistant' && msg.run_id);
      categorized = {
        liked: assistantMessages.filter(msg => msg.feedback?.type === 'liked'),
        disliked: assistantMessages.filter(msg => msg.feedback?.type === 'disliked'),
        noFeedback: assistantMessages.filter(msg => msg.feedback?.type === 'no_feedback'),
        other: assistantMessages.filter(msg => msg.feedback?.type === 'unknown')
      };
    }

    return { messages, sessionMemory, categorized };
  } catch (error) {
    console.error("Error fetching enhanced chat history v2:", error);
    throw error;
  }
}

/**
 * Get feedback statistics for a session
 */
export function getSessionFeedbackStats(
  sessionId: string,
  filteredSessionsResponse: AdminFilteredSessionsV2Response
): {
  totalMessages: number;
  likedCount: number;
  dislikedCount: number;
  noFeedbackCount: number;
  unknownCount: number;
} {
  const session = filteredSessionsResponse.data.find(s => s.session_id === sessionId);
  
  return {
    totalMessages: (session?.liked_message_count || 0) + 
                  (session?.disliked_message_count || 0) + 
                  (session?.no_feedback_message_count || 0),
    likedCount: session?.liked_message_count || 0,
    dislikedCount: session?.disliked_message_count || 0,
    noFeedbackCount: session?.no_feedback_message_count || 0,
    unknownCount: 0 // Messages not in any category
  };
}

// ============================================================================
// Master Memory Types & API Functions
// ============================================================================

export interface MasterMemoryItem {
  id: string;
  memory: string | null;
  user_id: string | null;
  agent_id: string | null;
  metadata: Record<string, any> | null;
  created_at: string | null;
  updated_at: string | null;
  score?: number | null;
}

export interface MasterMemoryListResponse {
  memories: MasterMemoryItem[];
  count: number;
}

export interface AddMasterMemoryPayload {
  user_id: string;
  agent_id?: string;
  text: string;
  metadata?: Record<string, any>;
}

export interface SearchMasterMemoryPayload {
  query: string;
  user_id: string;
  agent_id?: string;
  limit?: number;
}

export interface UpdateMasterMemoryPayload {
  text: string;
}

// Universal user ID for shared memories across all users
export const UNIVERSAL_USER_ID = "0";

/**
 * List all memories (universal + personal) for a user and agent
 */
export async function getMasterMemories(
  userId: string,
  agentId: string = "default"
): Promise<MasterMemoryListResponse> {
  try {
    const url = API_ENDPOINTS.MASTER_MEMORIES_BY_USER(userId, agentId);
    const response = await apiClient.get(url);

    if (response.status !== 200) {
      throw new Error(`Failed to fetch master memories: ${response.status}`);
    }

    return response.data;
  } catch (error: any) {
    console.error("[getMasterMemories] Error:", error);
    throw error;
  }
}

/**
 * Add a new memory (AI extraction runs to decide ADD vs NONE)
 */
export async function addMasterMemory(
  payload: AddMasterMemoryPayload
): Promise<MasterMemoryListResponse> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.MASTER_MEMORIES, payload);

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to add master memory: ${response.status}`);
    }

    await logAdminAction(
      `Added master memory for user ${payload.user_id}`,
      {
        url: '/liftai/master-memories',
        method: 'POST',
        status: response.status,
        category: 'settings',
        action: 'add_master_memory',
        userId: payload.user_id,
        agentId: payload.agent_id,
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("[addMasterMemory] Error:", error);
    await logAdminAction(`Failed to add master memory: ${error.message}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Semantic search across universal + personal memories
 */
export async function searchMasterMemories(
  payload: SearchMasterMemoryPayload
): Promise<MasterMemoryListResponse> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.MASTER_MEMORIES_SEARCH, {
      ...payload,
      limit: payload.limit || 10,
    });

    if (response.status !== 200) {
      throw new Error(`Failed to search master memories: ${response.status}`);
    }

    return response.data;
  } catch (error: any) {
    console.error("[searchMasterMemories] Error:", error);
    throw error;
  }
}

/**
 * Get a single memory by ID
 */
export async function getMasterMemory(memoryId: string): Promise<MasterMemoryItem | null> {
  try {
    const url = API_ENDPOINTS.MASTER_MEMORIES_SINGLE(memoryId);
    const response = await apiClient.get(url);

    if (response.status === 404) {
      return null;
    }

    if (response.status !== 200) {
      throw new Error(`Failed to get master memory: ${response.status}`);
    }

    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error("[getMasterMemory] Error:", error);
    throw error;
  }
}

/**
 * Update an existing memory's text
 */
export async function updateMasterMemory(
  memoryId: string,
  text: string
): Promise<MasterMemoryItem> {
  // Best-effort: capture the current memory text so the log can show old -> new.
  let previousText: string | undefined;
  try {
    const existing = await getMasterMemory(memoryId);
    previousText = (existing as any)?.text;
  } catch {
    previousText = undefined;
  }
  try {
    const url = `${API_ENDPOINTS.MASTER_MEMORIES}/${memoryId}`;
    const response = await apiClient.put(url, { text });

    if (response.status === 404) {
      throw new Error("Memory not found");
    }

    if (response.status !== 200) {
      throw new Error(`Failed to update master memory: ${response.status}`);
    }

    const truncate = (val?: string) =>
      val && val.length > 140 ? `${val.slice(0, 140)}…` : (val ?? '—');
    await logAdminAction(
      `Updated master memory ${memoryId}`,
      {
        url: `/liftai/master-memories/${memoryId}`,
        method: 'PUT',
        status: 200,
        category: 'settings',
        action: 'update_master_memory',
        memoryId,
        changes: [
          { label: 'Memory Text', from: truncate(previousText), to: truncate(text) },
        ],
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("[updateMasterMemory] Error:", error);
    await logAdminAction(`Failed to update master memory ${memoryId}: ${error.message}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Delete a single memory by ID
 */
export async function deleteMasterMemory(memoryId: string): Promise<boolean> {
  try {
    const url = API_ENDPOINTS.MASTER_MEMORIES_SINGLE(memoryId);
    const response = await apiClient.delete(url);

    if (response.status === 404) {
      throw new Error("Memory not found");
    }

    await logAdminAction(
      `Deleted master memory ${memoryId}`,
      {
        url: `/liftai/master-memories/single/${memoryId}`,
        method: 'DELETE',
        status: response.status,
        category: 'settings',
        action: 'delete_master_memory',
        memoryId,
      }
    );

    return response.status === 204 || response.status === 200;
  } catch (error: any) {
    console.error("[deleteMasterMemory] Error:", error);
    await logAdminAction(`Failed to delete master memory ${memoryId}: ${error.message}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Delete all memories for a user + agent combination
 */
export async function deleteAllMasterMemories(
  userId: string,
  agentId: string = "default"
): Promise<boolean> {
  try {
    const url = API_ENDPOINTS.MASTER_MEMORIES_BY_USER(userId, agentId);
    const response = await apiClient.delete(url);

    await logAdminAction(
      `Deleted all memories for user ${userId} (agent: ${agentId})`,
      {
        url: `/liftai/master-memories/${userId}/${agentId}`,
        method: 'DELETE',
        status: response.status,
        category: 'settings',
        action: 'delete_all_master_memories',
        userId,
        agentId,
      }
    );

    return response.status === 204 || response.status === 200;
  } catch (error: any) {
    console.error("[deleteAllMasterMemories] Error:", error);
    await logAdminAction(`Failed to delete all memories for user ${userId}: ${error.message}`, { category: 'settings' }, true);
    throw error;
  }
}

// ============================================================================
// Workflow Management API Functions
// ============================================================================

/**
 * Toggle workflow enabled/disabled state
 */
export async function toggleWorkflowDisabled(
  workflowId: string,
  isDisabled: boolean
): Promise<any> {
  try {
    console.log(`[toggleWorkflowDisabled] Setting workflow ${workflowId} is_disabled=${isDisabled}`);
    
    const response = await apiClient.put(
      API_ENDPOINTS.WORKFLOW_BY_ID(workflowId),
      { is_disabled: isDisabled }
    );

    if (response.status !== 200) {
      Toastify("error", `Failed to update workflow: ${response.status}`);
      throw new Error(`Failed to update workflow: ${response.status}`);
    }

    await logAdminAction(
      `${isDisabled ? 'Disabled' : 'Enabled'} workflow ${workflowId}`,
      {
        url: `/liftai/workflows/${workflowId}`,
        method: 'PUT',
        status: 200,
        category: 'settings',
        action: isDisabled ? 'disable_workflow' : 'enable_workflow',
        workflowId,
        changes: [
          {
            label: 'Status',
            from: isDisabled ? 'Enabled' : 'Disabled',
            to: isDisabled ? 'Disabled' : 'Enabled',
          },
        ],
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("[toggleWorkflowDisabled] Error:", error);
    Toastify("error", "Error updating workflow status");
    await logAdminAction(`Failed to ${isDisabled ? 'disable' : 'enable'} workflow ${workflowId}`, { category: 'settings' }, true);
    throw error;
  }
}

// ============================================================================
// Model Registry + Configuration API
// ============================================================================

export interface ModelConfigItem {
  role: string;
  label: string;
  description: string;
  default_model: string;
  provider: string;
  current_model: string;
  is_overridden: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface RegistryModel {
  model_id: string;
  provider: string;
  display_name: string;
  family: string;
  raw_family: string | null;
  capabilities: string[];
  speed: string | null;
  quality: string | null;
  cost_tier: string | null;
  context_window: number | null;
  max_output: number | null;
  input_cost: number | null;
  output_cost: number | null;
  reasoning: boolean;
  tool_call: boolean;
  release_date: string | null;
  knowledge_cutoff: string | null;
  enabled: boolean;
  /** Exposed in the Open LLM (vanilla) mode picker. Independent of `enabled`
   *  (which controls Lift Analysis availability) — a model can be flagged for
   *  either, both, or neither. */
  open_llm_enabled: boolean;
  source: string;
  updated_at: string | null;
}

// Keep the old name as an alias for backwards compat
export type VertexModel = RegistryModel;

/**
 * Get all model role configurations (DB overrides merged with defaults)
 */
export async function getModelConfigs(): Promise<ModelConfigItem[]> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN_MODEL_CONFIG);
    return response.data?.configs || [];
  } catch (error: any) {
    console.error("[getModelConfigs] Error:", error);
    throw error;
  }
}

/**
 * Update a model for a specific role (validates against registry)
 */
export async function updateModelConfig(
  role: string,
  modelId: string,
  updatedBy?: string
): Promise<any> {
  // Best-effort: capture the current model for this role so the log can show old -> new.
  let previousModel: string | undefined;
  try {
    const configs = await getModelConfigs();
    previousModel = configs.find((c) => c.role === role)?.current_model;
  } catch {
    previousModel = undefined;
  }
  try {
    const response = await apiClient.put(API_ENDPOINTS.ADMIN_MODEL_CONFIG, {
      role,
      model_id: modelId,
      updated_by: updatedBy,
    });
    if (response.status === 200) {
      Toastify("success", "Model configuration updated!");
      const modelChanged = previousModel !== undefined && previousModel !== modelId;
      await logAdminAction(
        `Model configuration for role "${role}" updated${
          modelChanged ? `: ${previousModel} → ${modelId}` : ''
        }`,
        {
          url: '/liftai/admin/v2/model-config',
          method: 'PUT',
          status: 200,
          category: 'settings',
          action: 'update_model_config',
          role,
          modelId,
          changes: [
            { label: 'Model', from: previousModel ?? 'Unknown', to: modelId },
          ],
        }
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("[updateModelConfig] Error:", error);
    const detail = error?.response?.data?.detail;
    Toastify("error", detail || "Failed to update model configuration");
    await logAdminAction(`Failed to update model config for role "${role}": ${detail}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Reset a role back to its default model
 */
export async function resetModelConfig(role: string): Promise<any> {
  try {
    const response = await apiClient.delete(
      API_ENDPOINTS.ADMIN_MODEL_CONFIG_ROLE(role)
    );
    if (response.status === 200) {
      Toastify("success", "Model reset to default!");
      await logAdminAction(
        `Reset model config for role "${role}" to default`,
        {
          url: `/liftai/admin/v2/model-config/${role}`,
          method: 'DELETE',
          status: 200,
          category: 'settings',
          action: 'reset_model_config',
          role,
        }
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("[resetModelConfig] Error:", error);
    Toastify("error", "Failed to reset model configuration");
    await logAdminAction(`Failed to reset model config for role "${role}"`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * List all models from the registry (DB-backed, not hardcoded)
 */
export async function getRegistryModels(
  enabledOnly = true,
  openLlmOnly = false,
): Promise<RegistryModel[]> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN_MODELS_REGISTRY, {
      params: { enabled_only: enabledOnly, open_llm_only: openLlmOnly },
    });
    return response.data?.models || [];
  } catch (error: any) {
    console.error("[getRegistryModels] Error:", error);
    throw error;
  }
}

// Backwards-compat alias
export const getVertexModels = () => getRegistryModels(true);

/**
 * Manually add a model to the registry
 */
export async function addRegistryModel(model: {
  model_id: string;
  provider: string;
  display_name: string;
  family: string;
  capabilities?: string[];
  speed?: string;
  quality?: string;
  cost_tier?: string;
  context_window?: number;
  enabled?: boolean;
  open_llm_enabled?: boolean;
}): Promise<any> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.ADMIN_MODELS_REGISTRY, model);
    if (response.status === 200) {
      Toastify("success", `Model "${model.display_name}" added to registry`);
      await logAdminAction(
        `Model "${model.display_name}" added to registry`,
        {
          url: '/liftai/admin/v2/models-registry',
          method: 'POST',
          status: 200,
          category: 'settings',
          action: 'add_registry_model',
          modelId: model.model_id,
          displayName: model.display_name,
          provider: model.provider,
          details: [
            { label: 'Display Name', value: model.display_name || 'Not Set' },
            { label: 'Model ID', value: model.model_id || 'Not Set' },
            { label: 'Provider', value: model.provider || 'Not Set' },
            ...(model.cost_tier ? [{ label: 'Cost Tier', value: model.cost_tier }] : []),
            ...(model.enabled !== undefined ? [{ label: 'Enabled', value: model.enabled ? 'Yes' : 'No' }] : []),
          ],
        }
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("[addRegistryModel] Error:", error);
    const detail = error?.response?.data?.detail;
    Toastify("error", detail || "Failed to add model");
    await logAdminAction(`Failed to add model "${model.display_name}" to registry: ${detail}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Update an existing registry model's metadata
 */
export async function updateRegistryModel(
  modelId: string,
  updates: Partial<Pick<RegistryModel, "display_name" | "capabilities" | "speed" | "quality" | "cost_tier" | "context_window" | "enabled" | "open_llm_enabled">>
): Promise<any> {
  // Best-effort: capture the model's current state so the log can show old -> new per field.
  let previousModel: RegistryModel | undefined;
  try {
    const models = await getRegistryModels(false, false);
    previousModel = models.find((m) => m.model_id === modelId);
  } catch {
    previousModel = undefined;
  }
  try {
    const response = await apiClient.put(
      API_ENDPOINTS.ADMIN_MODELS_REGISTRY_BY_ID(modelId),
      updates
    );
    if (response.status === 200) {
      Toastify("success", "Model updated");
      const fmt = (val: any) =>
        Array.isArray(val) ? val.join(', ') : String(val ?? '—');
      const changes = Object.entries(updates)
        .map(([key, value]) => ({
          label: key,
          from: fmt(previousModel ? (previousModel as any)[key] : undefined),
          to: fmt(value),
        }))
        .filter((c) => c.from !== c.to);
      const changeSummary = changes
        .map((c) => `${c.label}: ${c.from} → ${c.to}`)
        .join('; ');
      await logAdminAction(
        `Updated registry model "${modelId}"${changeSummary ? ` — ${changeSummary}` : ''}`,
        {
          url: `/liftai/admin/v2/models-registry/${modelId}`,
          method: 'PUT',
          status: 200,
          category: 'settings',
          action: 'update_registry_model',
          modelId,
          changes,
        }
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("[updateRegistryModel] Error:", error);
    const detail = error?.response?.data?.detail;
    Toastify("error", detail || "Failed to update model");
    await logAdminAction(`Failed to update registry model "${modelId}": ${detail}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Remove a model from the registry
 */
export async function deleteRegistryModel(modelId: string): Promise<any> {
  try {
    const response = await apiClient.delete(
      API_ENDPOINTS.ADMIN_MODELS_REGISTRY_BY_ID(modelId)
    );
    if (response.status === 200) {
      Toastify("success", "Model removed from registry");
      await logAdminAction(
        `Removed model "${modelId}" from registry`,
        {
          url: `/liftai/admin/v2/models-registry/${modelId}`,
          method: 'DELETE',
          status: 200,
          category: 'settings',
          action: 'delete_registry_model',
          modelId,
        }
      );
    }
    return response.data;
  } catch (error: any) {
    console.error("[deleteRegistryModel] Error:", error);
    const detail = error?.response?.data?.detail;
    Toastify("error", detail || "Failed to remove model");
    await logAdminAction(`Failed to remove model "${modelId}" from registry: ${detail}`, { category: 'settings' }, true);
    throw error;
  }
}

/**
 * Sync models from models.dev (Claude, Gemini, OpenAI)
 */
export async function syncRegistryModels(): Promise<{ added: number; updated: number; total: number }> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.ADMIN_MODELS_REGISTRY_SYNC);
    const data = response.data;
    if (data.added > 0 || data.updated > 0) {
      Toastify("success", `Sync complete: ${data.added} new, ${data.updated ?? 0} updated — ${data.total} total`);
    } else {
      Toastify("info", `Registry up to date — ${data.total} models`);
    }
    await logAdminAction(
      `Synced model registry: ${data.added} added, ${data.updated ?? 0} updated, ${data.total} total`,
      {
        url: '/liftai/admin/v2/models-registry/sync',
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'sync_registry_models',
        addedCount: data.added,
        updatedCount: data.updated ?? 0,
        totalCount: data.total,
      }
    );
    return data;
  } catch (error: any) {
    console.error("[syncRegistryModels] Error:", error);
    Toastify("error", "Failed to sync models from models.dev");
    await logAdminAction('Failed to sync model registry', { category: 'settings' }, true);
    throw error;
  }
}

export interface VertexAvailabilityResult {
  model_id: string;
  provider: string;
  /** true = enabled, false = NOT enabled, null = inconclusive (fail-open) */
  available: boolean | null;
  reason: string;
}

/**
 * Probe whether a model is actually enabled in this project's Vertex Model
 * Garden before assigning it to a mode/role. `available === false` means the
 * model must be enabled in the GCP console first; `null` means the check was
 * inconclusive (callers should fail-open).
 */
export async function checkVertexModelAvailability(
  modelId: string
): Promise<VertexAvailabilityResult> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.ADMIN_MODELS_REGISTRY_VERTEX_AVAILABILITY(modelId)
    );
    return response.data;
  } catch (error: any) {
    console.error("[checkVertexModelAvailability] Error:", error);
    // Fail-open on a flaky check so we never block a legitimate assignment.
    return {
      model_id: modelId,
      provider: "",
      available: null,
      reason: "Vertex availability check failed; proceeding without it.",
    };
  }
}
